import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { ProcessedInvoice } from '../types';
import InvoiceUploader from './invoice/InvoiceUploader';
import InvoiceGrid from './invoice/InvoiceGrid';
import ProcessingProgress from './invoice/ProcessingProgress';
import LoginPrompt from './auth/LoginPrompt';
import InstructionsSection from './instructions/InstructionsSection';
import { processInvoices } from '../lib/bulk/invoiceProcessor';
import { Upload } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function InvoiceProcessor() {
  const { isAuthenticated, isLoading, profile, updateInvoiceQuota } = useAuth();
  const [invoices, setInvoices] = useState<ProcessedInvoice[]>([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    setProcessing(true);
    setProgress({ current: 0, total: acceptedFiles.length });

    try {
      console.log('Starting invoice processing...', acceptedFiles.length, 'files');
      const results = await processInvoices(
        acceptedFiles, 
        (current, total) => {
          console.log('Processing progress:', current, '/', total);
          setProgress({ current, total });
        },
        (quota) => {
          console.log('Updating quota:', quota);
          updateInvoiceQuota(quota);
        }
      );
      console.log('Invoice processing completed:', results);
      setInvoices(results);
    } catch (error) {
      console.error('Failed to process files:', error);
    } finally {
      setProcessing(false);
    }
  }, [updateInvoiceQuota]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true
  });

  const handleDataChange = (id: string, data: PaymentData) => {
    setInvoices(prev => prev.map(invoice => 
      invoice.id === id ? { ...invoice, data } : invoice
    ));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      <div className="text-center">
        <h1 className="text-4xl font-medium text-[#fffaf6] mb-4">
          Easy Invoice Payment App
        </h1>
        <p className="text-xl text-[#86868b] mb-8">
          Upload and process multiple supplier invoices effortlessly. Extract payment details and generate QR codes compatible with{' '}
          <span className="text-[#c8c2bd]">Belgian, German, Austrian, and Finnish</span>{' '}
          banking systems.
        </p>
        {invoices.length > 0 && (
          <button
            onClick={() => setInvoices([])}
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Upload className="w-4 h-4" />
            New Upload
          </button>
        )}
      </div>

      {!isAuthenticated ? (
        <>
          <LoginPrompt />
          <InstructionsSection />
        </>
      ) : (
        <div className="space-y-6">
          {/* Credits display above file upload area */}
          <div className="bg-[#111] border border-gray-800 rounded-lg p-4 text-center">
            <p className="text-[#86868b]">
              Available Invoice Credits: <span className="text-[#c8c2bd] font-medium">{profile?.availableInvoiceBalance || 0}</span>
            </p>
          </div>

          {invoices.length === 0 ? (
            <div className="space-y-6">
              <InvoiceUploader
                getRootProps={getRootProps}
                getInputProps={getInputProps}
                isDragActive={isDragActive}
              />
              
              {processing && (
                <ProcessingProgress
                  current={progress.current}
                  total={progress.total}
                />
              )}

              <InstructionsSection />
            </div>
          ) : (
            <InvoiceGrid
              invoices={invoices}
              onDataChange={handleDataChange}
            />
          )}
        </div>
      )}
    </div>
  );
}